import React, { useState } from 'react';
import { AccountPageLayout } from 'pages/Account/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, CheckBox, Text } from '@clariness/clarikit-react';
import { themes } from '@clariness/clarikit-tokens';

import { ShouldRender } from 'components/ShouldRender';
import { MultipleInvestigatorNotificationUpdateDto } from 'common/api';
import { BoxLoader } from 'components/loaders';
import { formData } from './AccountNotification';
import { ChevronDownIcon, ChevronUpIcon } from '@clariness/clarikit-icons';

interface Props {
  formDataList: Array<formData>;
  notifications: MultipleInvestigatorNotificationUpdateDto;
  setNotifications: (value: MultipleInvestigatorNotificationUpdateDto) => void;
  isFetching: boolean;
  setIsSaveEnabled: (value: boolean) => void;
}

export const NewReferralNotification: React.FC<Props> = ({
  formDataList,
  isFetching,
  setIsSaveEnabled,
  notifications,
  setNotifications,
}: Props) => {
  const { space } = themes.light;
  const intl = useIntl();
  const [isShowStudies, setIsShowStudies] = useState(true);

  const isWeeklyChecked = (value: number): boolean => {
    return Boolean(
      notifications.investigatorNotificationUpdateDto.find(
        (investigator) => investigator.id === value
      )?.weeklyReferralRecipient
    );
  };

  const isInstantChecked = (value: number): boolean => {
    return Boolean(
      notifications.investigatorNotificationUpdateDto.find(
        (investigator) => investigator.id === value
      )?.referralRecipient
    );
  };

  const isAllChecked = (): boolean => {
    return Boolean(
      !notifications.investigatorNotificationUpdateDto.find(
        (investigator) =>
          !investigator.weeklyReferralRecipient ||
          !investigator.referralRecipient
      )
    );
  };

  const heading = intl.formatMessage({
    id: 'is.account_notification.new_referral_label',
    defaultMessage: 'New referral',
  });

  return (
    <AccountPageLayout>
      {isFetching ? (
        <BoxLoader />
      ) : (
        <Box display="flex" flexDirection="column" gap={space['space-16']}>
          <Box
            display="grid"
            gap={space['space-16']}
            alignItems="center"
            style={{
              gridTemplateColumns: '5fr 80fr 15fr',
              marginBottom: '10px',
            }}
          >
            <CheckBox
              value="allNotification"
              name="allNotification"
              label={heading}
              labelHidden
              checked={isAllChecked()}
              onChange={(checked) => {
                const updateData =
                  notifications.investigatorNotificationUpdateDto.map(
                    (investigator) => {
                      return {
                        ...investigator,
                        weeklyReferralRecipient: checked,
                        referralRecipient: checked,
                      };
                    }
                  );
                setNotifications({
                  investigatorNotificationUpdateDto: updateData,
                });
                setIsSaveEnabled(false);
              }}
              data-test-id="all-notifications-checkbox"
            />
            <Text
              as="h4"
              color={!isAllChecked() ? 'color-gray-variant-2' : undefined}
            >
              {heading}
            </Text>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="tertiary"
                color="color-gray-variant-1"
                iconLeft={isShowStudies ? ChevronUpIcon : ChevronDownIcon}
                accessibilityLabel={intl.formatMessage(
                  isShowStudies
                    ? {
                        id: 'is.account_notification.hide_studies',
                        defaultMessage: 'Hide studies',
                      }
                    : {
                        id: 'is.account_notification.show_studies',
                        defaultMessage: 'Show studies',
                      }
                )}
                ariaExpanded={isShowStudies}
                onClick={() => setIsShowStudies(!isShowStudies)}
                data-test-id="site-employee-back-button"
              />
            </Box>
            <Box />
            <Text
              as="p"
              color={!isAllChecked() ? 'color-gray-variant-2' : undefined}
            >
              <FormattedMessage
                id="is.account_notification.new_referral_paragraph"
                defaultMessage="E-mail notification, send on weekly basis with a list New Referrals in Investigator Portal"
              />
            </Text>
          </Box>

          <ShouldRender when={isShowStudies}>
            <Box display="flex" flexDirection="column" gap={space['space-16']}>
              {formDataList.map((formData) => {
                return (
                  <Box style={{ margin: '5px' }} key={formData.studyName}>
                    <Box
                      display="grid"
                      gap={space['space-16']}
                      style={{ gridTemplateColumns: '5fr 75fr 20fr' }}
                    >
                      <Box />
                      <Box style={{ marginBottom: '10px' }}>
                        <Text as="p" fontWeight="bold" breakWord>
                          {formData.studyName}
                        </Text>
                      </Box>
                      <Box />
                    </Box>
                    <Box
                      display="grid"
                      gap={space['space-16']}
                      style={{ gridTemplateColumns: '5fr 75fr 20fr' }}
                    >
                      <Box />
                      <Box />
                      <Box
                        display="flex"
                        gap={space['space-8']}
                        justifyContent="center"
                      >
                        <Text as="p" variant="bodySm">
                          Weekly
                        </Text>
                        <Text as="p" variant="bodySm">
                          Instant
                        </Text>
                      </Box>

                      {formData.value?.map((investigator) => (
                        <React.Fragment key={investigator.id}>
                          <Box />
                          <Box maxWidth="400px">
                            <Text
                              as="p"
                              breakWord
                              color={
                                !isWeeklyChecked(investigator.id) &&
                                !isInstantChecked(investigator.id)
                                  ? 'color-gray-variant-2'
                                  : undefined
                              }
                            >
                              {investigator.studyName} (
                              {investigator.studyLanguage})
                            </Text>
                          </Box>
                          <Box
                            display="flex"
                            gap={space['space-32']}
                            justifyContent="center"
                          >
                            <CheckBox
                              value={String(investigator.id)}
                              name="weeklyNotification"
                              label={`${investigator.studyName} (${investigator.studyLanguage}) – Weekly`}
                              labelHidden
                              checked={isWeeklyChecked(investigator.id)}
                              onChange={(checked) => {
                                const update =
                                  notifications.investigatorNotificationUpdateDto.map(
                                    (item) => {
                                      if (item.id === investigator.id) {
                                        return {
                                          ...item,
                                          weeklyReferralRecipient: checked,
                                        };
                                      }
                                      return { ...item };
                                    }
                                  );
                                setNotifications({
                                  investigatorNotificationUpdateDto: update,
                                });
                                setIsSaveEnabled(false);
                              }}
                            />

                            <CheckBox
                              value={String(investigator.id)}
                              name="instantNotification"
                              label={`${investigator.studyName} (${investigator.studyLanguage}) – Instant`}
                              labelHidden
                              checked={isInstantChecked(investigator.id)}
                              onChange={(checked) => {
                                const update =
                                  notifications.investigatorNotificationUpdateDto.map(
                                    (item) => {
                                      if (item.id === investigator.id) {
                                        return {
                                          ...item,
                                          referralRecipient: checked,
                                        };
                                      }
                                      return { ...item };
                                    }
                                  );
                                setNotifications({
                                  investigatorNotificationUpdateDto: update,
                                });
                                setIsSaveEnabled(false);
                              }}
                              data-test-id="instant-notification-checkbox"
                            />
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </ShouldRender>
        </Box>
      )}
    </AccountPageLayout>
  );
};
